import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiVisa } from 'blg-akaun-ts-lib';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { LabelService, Pagination } from 'blg-akaun-ts-lib';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { SharedUtilitiesHelperFunctions } from '../helper-functions';

@Component({
  selector: 'blg-select-multi-item-category-drop-down',
  templateUrl: './select-multi-item-category-drop-down.component.html',
  styleUrls: ['./select-multi-item-category-drop-down.component.css']
})

export class SelectMultiItemCategoryDropDownComponent implements OnInit, OnDestroy {

  @Input() itemCategoryLevel: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'Item Category';
  @Input() options: any;
  pagination: Pagination = new Pagination();

  @Output() itemCategoryLevelSelected = new EventEmitter<any>();

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = "ItemCategory"
  labelName = 'Item Category ';
  labelListGuid;

  protected _onDestroy = new Subject<void>();

  constructor(
    private labelService: LabelService,
    private readonly store: Store<SessionStates>
  ) { }

  ngOnInit() {
    this.key = "itemCategory" + this.getLevel();
    this.labelName += this.getLevel();

    // Combine subscriptions using combineLatest
    combineLatest([
      this.store.select(SessionSelectors.selectDropDownRowData),
      this.store.select(SessionSelectors.selectMasterSettings)
    ])
    .pipe(takeUntil(this._onDestroy))
    .subscribe(([resolvedRowData, resolveMasterSettings]) => {
      this.rowData = resolvedRowData ? resolvedRowData[this.key] || [] : [];
      this.labelListGuid = resolveMasterSettings['ITEM_CATEGORY_GROUP_' + this.getLevel()];

      if (this.rowData.length === 0) {
        this.getRowData(this.labelListGuid);
      } else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    });   

    // Listen for search field value changes with debouncing
    this.serverSideFiltering.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this._onDestroy)
      )
      .subscribe(() => {
        this.filtering();
      });
  }

  getRowData(labelListGuid, keyword?) {
    if (!labelListGuid) return;
  
    this.pagination.limit = 10;
    this.pagination.sortCriteria = [
      { columnName: 'orderBy', value: 'code' },
      { columnName: 'order', value: 'ASC' }
    ];
    this.pagination.conditionalCriteria = [
      { columnName: 'label_list_guid', operator: '=', value: labelListGuid },
    ];
    if (keyword) {
      this.pagination.conditionalCriteria.push({ columnName: 'search_word', operator: '=', value: keyword });
    }
  
    this.labelService.getByCriteria(this.pagination, this.apiVisa).subscribe(resolved => {
      // Map the data to only include code, name, and guid
      const transformedData = resolved.data.map(item => ({
        code: item.bl_fi_mst_label_hdr.code,
        name: item.bl_fi_mst_label_hdr.name,
        guid: item.bl_fi_mst_label_hdr.guid
      }));
  
      // Remove duplicates based on guid
      this.rowData = SharedUtilitiesHelperFunctions.removeDuplicate(transformedData, 'guid');
  
      // Sorting
      const sorted = this.rowData.sort((a, b) => (a.code < b.code ? -1 : 1));
  
      // Dispatch the sorted data
      this.store.dispatch(SessionActions.selectDropDownRowData({ key: this.key, rowData: this.rowData }));
  
      // Update the filteredOptions$ observable
      this.filteredOptions$.next(this.rowData.slice());
    });
  }
  

  filtering() {
    if (!this.rowData) {
      return;
    }

    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search || search.length < 3) {
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
      this.getRowData(this.labelListGuid, search);
    }
  }

  onSelect(guid: string) {
    if (guid) {
      this.itemCategoryLevel.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.itemCategoryLevelSelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  getLevel() {
    let value = this.options['level'];
    // console.log('level', value);
    if (value) {
      return value;
    }
    return "";  
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
